import CheckIcon from "@mui/icons-material/Check";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import LinkIcon from "@mui/icons-material/Link";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { CircularProgress } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import { useAuth } from "@/providers/auth";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import {
  ThreadVisibility,
  type AgentThreadRecord,
} from "@/shared/domain/agentThread";
import { useUpdateThreadVisibility } from "@/shared/domain/agentThread/hooks";
import { buildAgentThreadShareLink } from "@/shared/domain/agentThread/threadLinks";
import { Button, Popover } from "@/shared/foundation/primitives";
import { useCopyLink } from "@/shared/hooks/useCopyLink";

import styles from "./ThreadVisibilityControl.module.css";

interface ThreadVisibilityControlProps {
  thread: AgentThreadRecord;

  /**
   * `pill` renders icon plus word, for a metadata header with room for it.
   * `icon` renders the same icon alone at a fixed size, for the chat sidebar's
   * header, where the control sits in a row of 20px actions and must not grow
   * or reflow when the visibility changes.
   */
  variant: "pill" | "icon";
}

/** A lock reads "only me", a group reads "my org". Not an open/closed padlock
 *  pair: an open padlock reads as insecure rather than shared, and the two
 *  silhouettes are hard to tell apart at 20px. */
const VISIBILITY_ICON = {
  [ThreadVisibility.PRIVATE]: LockOutlinedIcon,
  [ThreadVisibility.ORG]: GroupOutlinedIcon,
} as const;

const VISIBILITY_LABEL = {
  [ThreadVisibility.PRIVATE]: "Private",
  [ThreadVisibility.ORG]: "Org",
} as const;

/** Read off the enum, in declaration order, so the picker offers every
 *  visibility the backend accepts without a second list to keep in step. */
const VISIBILITY_OPTIONS = Object.values(ThreadVisibility);

interface OptionStatusProps {
  selected: boolean;
  saving: boolean;
}

/**
 * Trailing glyph on a picker row: a spinner while that row's write is in
 * flight, otherwise a check on whichever row the thread currently is.
 *
 * The spinner takes the check's slot rather than sitting beside it, so the
 * rows keep their layout while a write is in flight. Nothing else in the popup
 * moves on a click — the picker only redraws once the write lands — so without
 * it a slow write is indistinguishable from a click that never registered.
 */
function OptionStatus({ selected, saving }: OptionStatusProps) {
  if (saving) {
    // Inherits the row's color rather than the check's accent: the row is not
    // the thread's visibility yet, and may never be if the write fails.
    return (
      <CircularProgress
        size={16}
        color="inherit"
        className={styles.optionSpinner}
        aria-label="Saving"
      />
    );
  }

  if (!selected) {
    return null;
  }

  return <CheckIcon className={styles.optionCheck} aria-hidden="true" />;
}

interface UnshareConfirmationProps {
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation for taking an org-readable thread back to private.
 *
 * Rendered inside the popover rather than as a dialog of its own, so Escape
 * and outside-click keep one meaning. Nothing moves or traps focus here, so it
 * is marked up as a labelled group; `alertdialog` would promise a focus move
 * that does not happen.
 */
function UnshareConfirmation({
  pending,
  onCancel,
  onConfirm,
}: UnshareConfirmationProps) {
  if (!pending) {
    return null;
  }

  return (
    <div
      className={styles.confirm}
      role="group"
      aria-label="Make thread private"
    >
      <p className={styles.confirmMessage}>
        Others will lose access to this thread
      </p>
      <div className={styles.confirmActions}>
        <Button size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={onConfirm}>
          Make private
        </Button>
      </div>
    </div>
  );
}

/**
 * Warns, under Copy link, that a private thread's link opens for nobody else.
 *
 * Without it the obvious next move (copy, paste into chat) hands a teammate a
 * link that fails, and the failure reaches them rather than whoever shared it.
 * An org thread needs no counterpart: a link that works for teammates is what
 * the reader already expects.
 */
function PrivateLinkCaveat({ visibility }: { visibility: ThreadVisibility }) {
  if (visibility !== ThreadVisibility.PRIVATE) {
    return null;
  }

  return <p className={styles.linkHint}>Only you can open this link</p>;
}

/**
 * The thread's visibility, and, for its creator, the control that changes it.
 *
 * The chat sidebar and the thread detail page render this one component, so
 * the same icon means the same thing in both.
 *
 * Opening it reveals the two states side by side (rather than a toggle whose
 * meaning depends on the current value) plus the thread's share link. "Share
 * this" and "get the link to share" are one intention, and splitting them in
 * two is how a user ends up pasting a link nobody else can open.
 *
 * Non-creators see the current state and can still copy the link; the picker
 * renders disabled rather than absent, so it is clear the setting exists and
 * belongs to someone else. The backend enforces the same rule.
 */
export function ThreadVisibilityControl({
  thread,
  variant,
}: ThreadVisibilityControlProps) {
  const { currentUser } = useAuth();
  const updateVisibility = useUpdateThreadVisibility();
  const [open, setOpen] = React.useState(false);
  // Held here rather than in a modal so the popover stays the only dismiss
  // layer.
  const [pendingUnshare, setPendingUnshare] = React.useState(false);
  const handleCopyLink = useCopyLink(
    buildAgentThreadShareLink(thread.thread_id),
  );

  const canEdit =
    currentUser !== null && thread.created_by === currentUser.user_id;
  // Nothing is patched until the write lands, so the picker still reads as the
  // old visibility while one is in flight: the row being written is the
  // mutation's own target, not the selected row.
  const savingVisibility = updateVisibility.isPending
    ? updateVisibility.variables?.visibility
    : undefined;
  const TriggerIcon = VISIBILITY_ICON[thread.visibility];
  // A private fork says so: the user who followed a shared link and forked it
  // is looking at their own copy, not the thread they were sent.
  const triggerLabel =
    thread.visibility === ThreadVisibility.PRIVATE &&
    thread.forked_from_thread_id
      ? "Private fork"
      : VISIBILITY_LABEL[thread.visibility];
  const triggerDescription = `Thread visibility: ${triggerLabel}`;
  const iconOnly = variant === "icon";

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setPendingUnshare(false);
    }
  };

  const saveVisibility = (visibility: ThreadVisibility) => {
    setPendingUnshare(false);
    updateVisibility.mutate({ threadId: thread.thread_id, visibility });
  };

  const handleSelect = (visibility: ThreadVisibility) => {
    if (!canEdit || visibility === thread.visibility) {
      return;
    }
    // Only the direction that takes access away needs confirming; sharing is
    // recoverable by the same control, one click away.
    if (visibility === ThreadVisibility.PRIVATE) {
      setPendingUnshare(true);
      return;
    }
    saveVisibility(visibility);
  };

  const trigger = (
    <button
      type="button"
      className={iconOnly ? styles.iconButton : styles.pill}
      aria-label={triggerDescription}
    >
      <TriggerIcon
        className={iconOnly ? styles.icon : styles.pillIcon}
        aria-hidden="true"
      />
      {iconOnly ? null : triggerLabel}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger
        render={
          <RobotoTooltip title={triggerDescription}>{trigger}</RobotoTooltip>
        }
      />
      <Popover.Content
        align="center"
        aria-label="Thread visibility"
        className={styles.popup}
      >
        <div
          className={styles.options}
          role="radiogroup"
          aria-label="Who can read this thread"
        >
          {VISIBILITY_OPTIONS.map((option) => {
            const OptionIcon = VISIBILITY_ICON[option];
            const selected = thread.visibility === option;
            return (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={selected}
                // The picker closes to new picks for as long as a write is
                // open. Two PATCHes racing over one thread's visibility settle
                // in whatever order the backend happens to finish them, and the
                // loser silently wins the UI.
                disabled={!canEdit || savingVisibility !== undefined}
                className={classNames(styles.option, {
                  [styles.isSelected]: selected,
                  [styles.isReadOnly]: !canEdit,
                })}
                onClick={() => handleSelect(option)}
              >
                <OptionIcon className={styles.optionIcon} aria-hidden="true" />
                <span className={styles.optionLabel}>
                  {VISIBILITY_LABEL[option]}
                </span>
                <OptionStatus
                  selected={selected}
                  saving={option === savingVisibility}
                />
              </button>
            );
          })}
        </div>

        <div className={styles.divider} />

        <UnshareConfirmation
          pending={pendingUnshare}
          onCancel={() => setPendingUnshare(false)}
          onConfirm={() => saveVisibility(ThreadVisibility.PRIVATE)}
        />

        <button
          type="button"
          className={styles.option}
          onClick={(e) => void handleCopyLink(e)}
        >
          <LinkIcon className={styles.optionIcon} aria-hidden="true" />
          <span className={styles.optionLabel}>Copy link</span>
        </button>
        <PrivateLinkCaveat visibility={thread.visibility} />
      </Popover.Content>
    </Popover>
  );
}
